/** Feature-owned Work Orders filter defaults and public API field mappings. */
import { type ReactElement } from "react";
import { UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  WO_PRIORITIES,
  WO_STATUSES,
  WO_TYPES,
  type WorkOrderFilters,
} from "@/features/workorders/queries";
import { type TableFacetOption } from "@/shared/components/table-facet";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import {
  TableToolbar,
  type TableFilterDefinition,
  type TableFilterValue,
} from "@/shared/components/table-toolbar";

type FilterKey = Exclude<keyof WorkOrderFilters, "q" | "asset_id">;

/** Bind shared controls to the Work Orders API, including status/open-only precedence. */
export function WorkOrderTableControls({
  filters,
  sites,
  sorting,
  onChange,
  onSortChange,
  onReset,
}: {
  filters: WorkOrderFilters;
  sites: TableFacetOption[];
  sorting?: string;
  onChange: (patch: Partial<WorkOrderFilters>) => void;
  onSortChange: (value: string | undefined) => void;
  onReset: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const options = (values: readonly string[], namespace: string): TableFacetOption[] =>
    values.map((value) => ({ value, label: t(`${namespace}.${value}`) }));
  const update = (key: FilterKey, value: TableFilterValue): void =>
    onChange({
      [key]: value,
      ...(key === "status"
        ? { open_only: false }
        : key === "open_only" && value === true
          ? { status: undefined }
          : {}),
    });
  const base = (
    key: FilterKey,
    label: string,
  ): {
    id: string;
    label: string;
    value: TableFilterValue;
    onChange: (value: TableFilterValue) => void;
    onRemove: () => void;
  } => ({
    id: key,
    label,
    value: filters[key],
    onChange: (value) => update(key, value),
    onRemove: () => update(key, key === "open_only" ? false : undefined),
  });
  const enums = (
    key: "status" | "priority" | "type",
    values: readonly string[],
    namespace: string,
  ): TableFilterDefinition => ({
    ...base(key, t(`app.${key}`)),
    kind: key === "type" ? "select" : "multi",
    prominent: key !== "type",
    options: options(values, namespace),
    removeLabel: filters[key]
      ?.split(",")
      .map((value) => t(`${namespace}.${value}`))
      .join(", "),
  });
  const boolean = (
    key: "open_only" | "overdue" | "mine",
    label: string,
  ): TableFilterDefinition => ({
    ...base(key, label),
    kind: "boolean",
    active: filters[key] === true,
    prominent: key === "mine",
    icon: key === "mine" ? UserRound : undefined,
    removeLabel: label,
  });
  const definitions: TableFilterDefinition[] = [
    enums("status", WO_STATUSES, "workOrders.status"),
    enums("priority", WO_PRIORITIES, "workOrders.priority"),
    enums("type", WO_TYPES, "workOrders.type"),
    { ...base("site_id", t("app.site")), kind: "select", options: sites },
    boolean("open_only", t("workOrders.openOnly")),
    boolean("overdue", t("workOrders.overdueOnly")),
    boolean("mine", t("workOrders.mine")),
    { ...base("due_after", t("tablePilot.dueFrom")), kind: "date", boundary: "start" },
    { ...base("due_before", t("tablePilot.dueUntil")), kind: "date", boundary: "end" },
  ];
  const invalidRange = !!(
    filters.due_after &&
    filters.due_before &&
    Date.parse(filters.due_after) > Date.parse(filters.due_before)
  );
  return (
    <TableToolbar
      filters={definitions}
      search={filters.q}
      searchPlaceholder={t("tablePilot.workOrderSearch")}
      searchDescription={t("tablePilot.workOrderSearchScope")}
      onSearchChange={(q) => onChange({ q })}
      sorting={sorting}
      sortFields={[
        "code",
        "title",
        "asset",
        "type",
        "priority",
        "status",
        "due_at",
        "created_at",
      ].map((value) => ({
        value,
        label: t(
          value === "created_at" ? "app.created" : value === "due_at" ? "app.due" : `app.${value}`,
        ),
      }))}
      onSortingChange={onSortChange}
      onReset={onReset}
      validationMessage={invalidRange ? t("tablePilot.invalidRange") : undefined}
      actions={
        <TableExportButton
          onExport={(format) =>
            downloadTableExport(
              "/api/v1/workorders/",
              "work-orders",
              { ...filters, ordering: sorting },
              format,
            )
          }
        />
      }
    />
  );
}
